// =============================================
// tagStore — the account's tag dictionary (/api/tags, user_tag, migration 137)
// =============================================
// One list of tags per ACCOUNT OWNER — own books, family, businesses and clients
// alike — so whatever was typed as a tag anywhere is suggested everywhere. A record
// keeps its own tags in its `tags` column as a comma list ("rental, 118 1SNW"); this
// store only knows which tags exist, to suggest them.
//
// Loaded once per page load and shared by every TagInput. Recording is best-effort:
// a tag that fails to reach the dictionary is still saved on its record.
// =============================================

import apiClient from './api/apiClient';

export const MAX_TAG_LEN = 60;

export interface TagEntry { tag_name: string; use_count: number }

let cache: TagEntry[] | null = null;
let loading: Promise<TagEntry[]> | null = null;
const listeners = new Set<(tags: TagEntry[]) => void>();

function emit() {
  const tags = cache || [];
  listeners.forEach(l => l(tags));
}

/** "a,  b ,,A" → ['a', 'b'] — trimmed, no blanks, one per spelling-insensitive name. */
export function parseTags(value: string | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  String(value || '').split(',').forEach(t => {
    const name = t.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LEN);
    if (name && !seen.has(name.toLowerCase())) { seen.add(name.toLowerCase()); out.push(name); }
  });
  return out;
}

/** ['a', 'b'] → "a, b" (null when empty) — how tags are stored on a record. */
export function joinTags(tags: string[]): string | null {
  return tags.length ? tags.join(', ') : null;
}

/** The account's tags, most used first. Empty when offline / signed out. */
export function loadTags(): Promise<TagEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (!loading) {
    loading = apiClient.get<TagEntry[]>('/tags')
      .then(res => {
        cache = Array.isArray(res.data) ? res.data.map(t => ({ tag_name: String(t.tag_name), use_count: Number(t.use_count) || 0 })) : [];
        emit();
        return cache;
      })
      .catch(() => { loading = null; return [] as TagEntry[]; });
  }
  return loading;
}

/** Run `fn` with the tag list now and whenever it changes. Returns an unsubscribe. */
export function subscribeTags(fn: (tags: TagEntry[]) => void): () => void {
  listeners.add(fn);
  if (cache) fn(cache); else void loadTags();
  return () => { listeners.delete(fn); };
}

/**
 * Record the tags a record now carries that it did NOT carry before (`before` = its
 * old comma list) — new names join the dictionary, known ones rank higher. Call after
 * the record itself is saved.
 */
export function recordTagUse(now: string | null | undefined, before?: string | null): void {
  const old = new Set(parseTags(before).map(t => t.toLowerCase()));
  const added = parseTags(now).filter(t => !old.has(t.toLowerCase()));
  if (!added.length) return;
  // show them straight away; the server copy follows
  const list = [...(cache || [])];
  added.forEach(t => {
    const hit = list.find(x => x.tag_name.toLowerCase() === t.toLowerCase());
    if (hit) hit.use_count += 1; else list.push({ tag_name: t, use_count: 1 });
  });
  cache = list.sort((a, b) => b.use_count - a.use_count || a.tag_name.localeCompare(b.tag_name));
  emit();
  apiClient.post('/tags/use', { tags: added }).catch(() => { /* the record keeps its tags regardless */ });
}
